import { Box, Flex } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import { MicOnIcon } from '#features/ui';
import { VoiceWave } from '../VoiceIndicator/VoiceWave';

/**
 * `inVoice` — в комнате кто-то есть и место в составе ещё свободно, `inVoiceFull` — то же самое
 * при закрытом составе (обещать вход нельзя), `open` — голос открыт и пуст, `beforeStart` —
 * сессия ещё не началась и комната откроется позже.
 *
 * Вариантов для автора-одиночки здесь больше нет (21.09.2026): пока в составе один человек,
 * голос не показывается вовсе - ни блоком, ни подписью (правило живёт в `SessionVoice`).
 * Подпись на целый ярус ради не-действия того не стоила.
 */
export type VoiceObserverVariant =
  | 'inVoice'
  | 'inVoiceFull'
  | 'open'
  | 'beforeStart';

type VoiceObserverNoteProps = {
  variant: VoiceObserverVariant;
};

/**
 * Голос глазами того, кто в состав не входит. Это подпись, а не блок с действием: войти он всё
 * равно не может, а ряд с иконкой в кружке, заголовком и пустым местом справа читался как
 * сломанная кнопка — и звал «заходи первым» туда, куда входа нет.
 *
 * Хвост про вступление здесь главное: он объясняет, почему кнопки нет, и отсылает к «Вступить»
 * строкой выше. Обещание честное — join мгновенный, одобрения автора не требует. Слова взяты у
 * Discord-режима (`post:session.voiceAfterJoin`): одна и та же ситуация в двух режимах голоса
 * должна называться одинаково.
 */
export const VoiceObserverNote = ({ variant }: VoiceObserverNoteProps) => {
  const { t } = useLingui();

  const isTalking = variant === 'inVoice' || variant === 'inVoiceFull';

  // Хвост про вступление ставим только там, где вступление возможно: при закрытом составе он
  // обещал бы дверь, которой нет, — та самая ошибка, ради которой блок и переписывался.
  // У ещё не открытой комнаты хвост свой: «откроется после вступления» рядом с «откроется к
  // старту» дважды обещало бы одно и то же разными сроками.
  const text = (() => {
    switch (variant) {
      case 'inVoiceFull':
        return t`post:voice.note.live`;
      case 'beforeStart':
        return `${t`post:voice.note.beforeStart`} · ${t`post:voice.note.squadNeeded`}`;
      case 'inVoice':
        return `${t`post:voice.note.live`} · ${t`post:voice.note.squadOnly`}`;
      default:
        return `${t`post:voice.note.open`} · ${t`post:voice.note.squadOnly`}`;
    }
  })();

  return (
    <Flex
      data-testid="voice-observer-note"
      align="center"
      gap={2}
      // Без горизонтального отступа — по той же причине, что и у блока комнаты рядом: подпись
      // вставляется в колонку карточки сессии, у которой свой px={4}, и собственный отступ уводил
      // её вправо относительно состава и кнопки выше. Раньше это видел только наблюдатель, теперь
      // подпись достаётся и автору, у которого над ней стоит ряд состава.
      px={0}
      py={2.5}
      borderTop="1px solid"
      borderColor="border.subtle"
      color="fg.secondary"
      textStyle="caption"
    >
      <Flex align="center" flex="none" color={isTalking ? undefined : 'fg.muted'}>
        {isTalking ? <VoiceWave height={10} /> : <MicOnIcon fontSize="14px" />}
      </Flex>
      <Box as="span" minW={0}>
        {text}
      </Box>
    </Flex>
  );
};
